import { cn, PageTabs, PageTabsList, PageTabsTrigger, Switch } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { PageTabsHeader } from "../../../components";
import { Link, useLocation } from "react-router-dom";
import { IMPLEMENTED_NOTIFICATION_CONFIGS } from "../../../constants";
import { useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";

export const PreferencesSettingsPage = () => {
  const { t } = useTranslation("settings");
  const location = useLocation();
  const { currentUser, selectedOrganization } = useAuth();

  const updateUserMutation = useTellescopeMutation({
    model: "users",
    method: "updateOne",
  });

  const handleCheckedChange = (id: string, type: "email" | "browser", value: boolean) => {
    return updateUserMutation.mutateAsync({
      id: currentUser.id,
      notificationPreferences: {
        [id]: {
          [type]: value,
        },
      },
    });
  };

  const handlePortalSettingChange = (key: "showAvatar" | "showDisplayName", value: boolean) => {
    return updateUserMutation.mutateAsync({
      id: currentUser.id,
      portalSettings: { ...currentUser.portalSettings, [key]: value },
    });
  };

  const showZendesk = selectedOrganization?.hasConnectedZendesk && selectedOrganization?.ticketThreadsEnabled;

  return (
    <>
      <PageTabsHeader>
        <PageTabs defaultValue="Preferences" value="Preferences" className="h-full">
          <PageTabsList>
            <PageTabsTrigger asChild value="Preferences">
              <Link to="settings/other/preferences">{t("Preferences")}</Link>
            </PageTabsTrigger>
          </PageTabsList>
        </PageTabs>
      </PageTabsHeader>
      <div className="flex-1 overflow-auto">
        <div className="flex flex-col">
          <div className="px-6 pt-6 pb-4 border-b">
            <h1 className="text-2xl font-semibold mb-1">{t("Preferences")}</h1>
            <p className="text-sm text-muted-foreground">{t("User level preference settings")}</p>
          </div>
          <div className="p-6 space-y-6">
            {IMPLEMENTED_NOTIFICATION_CONFIGS.map((section, i) => {
              const gridLayout = cn("grid grid-cols-[1fr_auto_auto] gap-2");

              return (
                <div className="grid divide-y" key={section.sectionTitle}>
                  <span className={cn(gridLayout, "text-muted-foreground font-medium pb-1")}>
                    <h2>{section.sectionTitle}</h2>
                    {i === 0 && (
                      <>
                        <p className="px-2">{t("Browser")}</p>
                        <p className="px-2">{t("Email")}</p>
                      </>
                    )}
                  </span>
                  <div className="py-3">
                    {section.settings.map((setting) => {
                      if (setting.id.toLowerCase().includes("zendesk") && !showZendesk) return null;

                      return (
                        <span className={cn(gridLayout, "py-2.5")} key={setting.id}>
                          <div className="flex gap-2 items-center">
                            {typeof setting.icon === "object" && "getSrc" in setting.icon ? (
                              <img
                                src={setting.icon.getSrc(location)}
                                className="text-muted-foreground h-[20px] w-[20px] p-0.5 opacity-40"
                              />
                            ) : (
                              <setting.icon className="text-muted-foreground h-[20px] w-[20px]" />
                            )}
                            <p className="font-medium">{setting.label}</p>
                          </div>
                          <div className="px-5">
                            <Switch
                              checked={
                                currentUser.notificationPreferences?.[setting.id]?.browser ?? setting.defaultValue
                              }
                              onCheckedChange={(value) => handleCheckedChange(setting.id, "browser", value)}
                            />
                          </div>
                          <div className="px-3">
                            <Switch
                              checked={currentUser.notificationPreferences?.[setting.id]?.email ?? setting.defaultValue}
                              onCheckedChange={(value) => handleCheckedChange(setting.id, "email", value)}
                            />
                          </div>
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="grid divide-y">
              <span className="text-muted-foreground font-medium pb-1">
                <h2>{t("Portal Presence")}</h2>
              </span>
              <div className="py-3 space-y-1">
                <span className="flex items-center justify-between py-2.5">
                  <p className="font-medium">{t("Show my avatar in patient chat")}</p>
                  <Switch
                    checked={currentUser.portalSettings?.showAvatar === true}
                    onCheckedChange={(value) => handlePortalSettingChange("showAvatar", value)}
                  />
                </span>
                <span className="flex items-center justify-between py-2.5">
                  <p className="font-medium">{t("Show my display name in patient chat")}</p>
                  <Switch
                    checked={currentUser.portalSettings?.showDisplayName === true}
                    onCheckedChange={(value) => handlePortalSettingChange("showDisplayName", value)}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
